/**
 * SpendCapPanel Component (CAP-TOGGLE-1, BL-b544aa26)
 * AIDLC Administrative Backend
 *
 * The agent spend cap on the Tokens page: today's and this month's spend against the cap, and
 * one switch, "Enforce spend caps". Off, caps still track and alert but refuse nothing, and an
 * amber banner says who turned them off, when and why. Changing the switch asks for a reason
 * first; only an Admin signed in with Microsoft may change it (the server decides, and its
 * refusal is shown).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import {
  fetchSpendCap,
  setSpendCapEnforcement,
  spendCapErrorText,
  capDollars,
  dollars,
  MIN_ENFORCEMENT_REASON_CHARS,
  MAX_ENFORCEMENT_REASON_CHARS,
  type PeriodCapState,
  type SpendCapState,
} from '../../services/spendCapService';

const REFRESH_INTERVAL_MS = 60000;
const PERIOD_LABELS: Record<PeriodCapState['period'], string> = { day: 'Today', month: 'This month' };

const dateText = (iso: string): string => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/** The amber banner while caps are off: who turned them off, when and why, when that is known. */
function notEnforcedText(state: SpendCapState): string {
  const head = 'Spend caps are not enforced. Costs are still tracked.';
  const change = state.enforcement;
  if (!change || change.enforce) return head;
  return `${head} Turned off by ${change.changedBy} on ${dateText(change.changedAt)}: ${change.reason}`;
}

function PeriodLine({ period }: { period: PeriodCapState }) {
  const tone = period.level === 'reached' ? 'text-red-700' : period.level === 'warning' ? 'text-amber-700' : 'text-gray-700';
  return (
    <p className={`text-sm ${tone}`}>
      <span className="font-medium">{PERIOD_LABELS[period.period]}:</span> {dollars(period.spentUsd)} of {capDollars(period.capUsd)} ({period.pct}%)
    </p>
  );
}

interface DialogProps {
  turningOn: boolean;
  onClose: () => void;
  onChanged: (state: SpendCapState) => void;
}

function EnforcementDialog({ turningOn, onClose, onChanged }: DialogProps) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const trimmed = reason.trim();
    if (trimmed.length < MIN_ENFORCEMENT_REASON_CHARS) {
      setError(`Give a reason of at least ${MIN_ENFORCEMENT_REASON_CHARS} characters.`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      onChanged(await setSpendCapEnforcement(turningOn, trimmed));
    } catch (err) {
      setError(spendCapErrorText(err, 'The setting could not be changed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <form onSubmit={submit} className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4" role="dialog" aria-modal="true" aria-labelledby="cap-enforce-title">
        <h3 id="cap-enforce-title" className="text-lg font-bold text-ldaja-black">
          {turningOn ? 'Turn spend caps back on?' : 'Turn off spend caps?'}
        </h3>
        <p className="text-sm text-gray-600">
          {turningOn
            ? 'New agent sessions will be refused once today\'s or this month\'s cap is reached.'
            : 'No agent session will be refused. Spend is still recorded and the 80% and 100% alerts are still sent.'}
        </p>
        <label className="block text-sm font-medium text-gray-700">
          Reason
          <textarea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setError(null);
            }}
            maxLength={MAX_ENFORCEMENT_REASON_CHARS}
            rows={3}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            autoFocus
          />
        </label>
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="min-h-[44px] px-4 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="min-h-[44px] px-4 py-2 text-sm rounded-md bg-ldaja-black text-white hover:opacity-90 disabled:opacity-50">
            {saving ? 'Saving…' : turningOn ? 'Turn on' : 'Turn off'}
          </button>
        </div>
      </form>
    </div>
  );
}

export function SpendCapPanel() {
  const { isAdmin } = useAuth();
  const [state, setState] = useState<SpendCapState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setState(await fetchSpendCap());
      setLoadError(null);
    } catch (err) {
      setLoadError(spendCapErrorText(err, 'The spend cap could not be loaded'));
    }
  }, []);

  useEffect(() => {
    void load();
    const interval = setInterval(() => void load(), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load]);

  if (loadError && !state) return <p className="text-sm text-red-600" role="alert">{loadError}</p>;
  if (!state) return <p className="text-sm text-gray-500">Loading the spend cap…</p>;

  return (
    <div className="space-y-3">
      {!state.enforce && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
          {notEnforcedText(state)}
        </div>
      )}
      <PeriodLine period={state.day} />
      <PeriodLine period={state.month} />
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-gray-100 pt-3">
        <div className="min-w-0 flex-1 basis-40">
          <p className="text-sm font-medium text-ldaja-black" id="cap-enforce-label">Enforce spend caps</p>
          <p className="text-xs text-gray-500">
            {state.enforce ? 'On: new agent sessions stop when a cap is reached.' : 'Off: caps warn but never stop a session.'}
            {!isAdmin && ' Only an Admin can change this.'}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={state.enforce}
          aria-labelledby="cap-enforce-label"
          disabled={!isAdmin}
          onClick={() => setDialogOpen(true)}
          className="flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${state.enforce ? 'bg-ldaja-blue' : 'bg-gray-300'}`}>
            <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${state.enforce ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </span>
        </button>
      </div>
      {loadError && <p className="text-sm text-red-600" role="alert">{loadError}</p>}
      {dialogOpen && (
        <EnforcementDialog
          turningOn={!state.enforce}
          onClose={() => setDialogOpen(false)}
          onChanged={(next) => {
            setState(next);
            setDialogOpen(false);
          }}
        />
      )}
    </div>
  );
}
